import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

// --- iFrameResize type definition for window ---
declare global {
  interface Window {
    iFrameResize: (options: object, selector: string) => void;
  }
}

// --- Props ---
interface PaytrIframeModalProps {
  isOpen: boolean;
  token: string;
  onClose: () => void;
}

const PaytrIframeModal: React.FC<PaytrIframeModalProps> = ({
  isOpen,
  token,
  onClose,
}) => {
  const { t } = useTranslation();
  const iframeSrc = `https://www.paytr.com/odeme/guvenli/${token}`;

  // --- Load and initialize iFrameResizer script ---
  useEffect(() => {
    if (!isOpen) return;

    const scriptId = "paytr-iframe-resizer-script";
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;

    const initializeResizer = () => {
      // Check if the iFrameResize function is available on the window object
      if (typeof window.iFrameResize === "function") {
        try {
          // Initialize the resizer on the iframe
          window.iFrameResize(
            { log: false, checkOrigin: false },
            "#paytr-iframe"
          );
          console.log("PayTR iFrameResizer initialized.");
        } catch (error) {
          console.error("Error initializing PayTR iFrameResizer:", error);
        }
      } else {
        console.warn("iFrameResize function not found on window.");
      }
    };

    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://www.paytr.com/js/iframeResizer.min.js";
      script.async = true;
      script.onload = initializeResizer;
      document.body.appendChild(script);
    } else {
      // If script already exists, just initialize the resizer
      initializeResizer();
    }

    return () => {
      // Optional: Cleanup script if component unmounts while modal is open
      // This might not be necessary if the modal closes before unmounting.
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        // You might decide not to remove it to avoid re-downloading
      }
    };
  }, [isOpen, token]); // Rerun effect if modal opens or token changes

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div className="paytr-iframe-modal-overlay">
      <div className="paytr-iframe-modal-content">
        <div className="paytr-iframe-modal-header">
          <h3>{t("checkout.payment.paytr_iframe.title")}</h3>
          <button
            onClick={onClose}
            className="paytr-iframe-modal-close"
            aria-label={t("common.close")}
          >
            ×
          </button>
        </div>
        <div className="paytr-iframe-container">
          <iframe
            id="paytr-iframe"
            src={iframeSrc}
            frameBorder="0"
            scrolling="no"
            style={{ width: "1px", minWidth: "100%" }}
            title={t("checkout.payment.paytr_iframe.title")}
          ></iframe>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PaytrIframeModal;
